package com.tovtalk.ui.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.ReportReasonOption
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.domain.repository.SettingsRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringKeys
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.launch

@Composable
fun ReportProfileDialog(
    userId: Int,
    onDismiss: () -> Unit,
    onReported: () -> Unit = {},
    postRepository: PostRepository = koinInject(),
    userRepository: UserRepository = koinInject(),
    settingsRepository: SettingsRepository = koinInject(),
) {
    var reasons by remember { mutableStateOf<List<ReportReasonOption>>(emptyList()) }
    var loadingReasons by remember { mutableStateOf(true) }
    var selectedReasonId by remember { mutableStateOf<Int?>(null) }
    var details by remember { mutableStateOf("") }
    var submitting by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        val lang = settingsRepository.getAppLanguage()
        when (val result = postRepository.getReportReasons("user", lang)) {
            is DomainResult.Success -> {
                reasons = result.data
                selectedReasonId = result.data.firstOrNull()?.id
                loadingReasons = false
            }
            is DomainResult.Error -> {
                errorMessage = result.message
                loadingReasons = false
            }
            else -> {}
        }
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(StringProvider.get(StringKeys.REPORT_PROFILE_TITLE)) },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (loadingReasons) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.Center
                    ) {
                        CircularProgressIndicator(
                            modifier = Modifier.padding(16.dp),
                            color = TovTalkTheme.colors.brand
                        )
                    }
                } else if (errorMessage != null) {
                    Text(
                        text = errorMessage!!,
                        color = TovTalkTheme.colors.error,
                        style = TovTalkTheme.typography.bodyMedium
                    )
                } else {
                    Column(
                        modifier = Modifier
                            .verticalScroll(rememberScrollState())
                            .heightIn(max = 320.dp)
                    ) {
                        Text(
                            text = StringProvider.get("report_reel_choose_reason"),
                            style = TovTalkTheme.typography.labelMedium,
                            color = TovTalkTheme.colors.textSecondary
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        reasons.forEach { reason ->
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable { selectedReasonId = reason.id }
                                    .padding(vertical = 4.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                RadioButton(
                                    selected = selectedReasonId == reason.id,
                                    onClick = { selectedReasonId = reason.id }
                                )
                                Spacer(modifier = Modifier.padding(start = 8.dp))
                                Text(
                                    text = reason.title,
                                    style = TovTalkTheme.typography.bodyMedium,
                                    color = TovTalkTheme.colors.textPrimary
                                )
                            }
                        }
                        Spacer(modifier = Modifier.height(12.dp))
                        OutlinedTextField(
                            value = details,
                            onValueChange = { details = it },
                            modifier = Modifier.fillMaxWidth(),
                            label = { Text(StringProvider.get("report_reel_details_optional")) },
                            maxLines = 3,
                            singleLine = false
                        )
                    }
                }
            }
        },
        confirmButton = {
            if (!loadingReasons && errorMessage == null) {
                Button(
                    onClick = {
                        val reasonId = selectedReasonId ?: return@Button
                        submitting = true
                        scope.launch {
                            when (val result = userRepository.reportUser(userId, reasonId, details)) {
                                is DomainResult.Success -> {
                                    onReported()
                                    onDismiss()
                                }
                                is DomainResult.Error -> {
                                    errorMessage = result.message
                                    submitting = false
                                }
                                else -> { submitting = false }
                            }
                        }
                    },
                    enabled = !submitting && selectedReasonId != null
                ) {
                    if (submitting) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(20.dp),
                            color = TovTalkTheme.colors.onBrand
                        )
                    } else {
                        Text(StringProvider.get("report_reel_submit"))
                    }
                }
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(StringProvider.get("common_cancel"))
            }
        }
    )
}
